import { defineStore } from 'pinia'
import messagesApi from '@/api/messagesApi.js'
import { useConversationsStore } from './conversations.js'

const LIMIT = 50

export const useChatStore = defineStore('chatStore', {
    state: () => ({
        conversationId: null,
        messages: [],
        beforeId: null,
        hasMore: true,
        loading: false,
        sending: false,
        isActive: false,
    }),
    getters: {
        lastMessageId: (s) => s.messages.at(-1)?.id ?? null,
    },
    actions: {
        async init(id) {
            this.conversationId = Number(id)
            this.messages = []
            this.beforeId = null
            this.hasMore = true
            this.loading = false
            this.isActive = true
            await this.fetchLatest()
            await this.markRead()
            const convs = useConversationsStore()
            convs.setActive(this.conversationId)
            const conv = convs.items.find(c => c.id === this.conversationId)
            if (conv) conv.unread_count = 0
        },
        async fetchLatest() {
            if (!this.conversationId) return
            this.loading = true
            try {
                const data = await messagesApi.getMessages(this.conversationId, { limit: LIMIT })
                this.messages = Array.isArray(data) ? data : []
                if (this.messages.length) this.beforeId = Math.min(...this.messages.map(m => m.id))
                this.hasMore = this.messages.length === LIMIT
            } finally { this.loading = false }
        },
        async fetchMore() {
            if (!this.conversationId || !this.hasMore || this.loading) return
            this.loading = true
            try {
                const older = await messagesApi.getMessages(this.conversationId, { before_id: this.beforeId, limit: LIMIT })
                if (older?.length) {
                    this.beforeId = Math.min(this.beforeId ?? Infinity, ...older.map(m => m.id))
                    this.messages = [...older, ...this.messages]
                } else {
                    this.hasMore = false
                }
            } finally { this.loading = false }
        },
        async send(body, context) {
            if (!this.conversationId || !body?.trim()) return
            this.sending = true
            const tempId = `tmp_${Date.now()}`
            const optimistic = { id: tempId, body, created_at: new Date().toISOString(), pending: true, ...(context||{}) }
            this.messages.push(optimistic)
            try {
                const real = await messagesApi.sendMessage(this.conversationId, { body, ...(context||{}) })
                const idx = this.messages.findIndex(m => m.id === tempId)
                if (idx !== -1) this.messages[idx] = real
                useConversationsStore().upsertFromMessageEvent({ conversation_id: this.conversationId, message: real })
                await this.markRead()
            } catch (e) {
                const idx = this.messages.findIndex(m => m.id === tempId)
                if (idx !== -1) this.messages[idx] = { ...optimistic, pending: false, error: true }
                throw e
            } finally { this.sending = false }
        },
        async markRead() {
            if (!this.isActive || !this.conversationId || !this.messages.length) return
            const lastId = this.messages.at(-1)?.id
            if (!lastId || String(lastId).startsWith('tmp_')) return
            try {
                await messagesApi.markRead(this.conversationId, lastId)
                const conv = useConversationsStore().items.find(c => c.id === this.conversationId)
                if (conv) conv.unread_count = 0
            } catch { /* noop */ }
        },
        async setActive(flag) {
            this.isActive = !!flag
            if (flag) await this.markRead()
        },
        async applyWsEvent(payload) {
            if (!payload?.type || payload.conversation_id !== this.conversationId) return
            if (payload.type === 'message.created') {
                try {
                    const data = await messagesApi.getMessages(this.conversationId, { limit: 1 })
                    const msg = data?.at(-1)
                    if (msg && !this.messages.some(m => m.id === msg.id)) this.messages.push(msg)
                    await this.markRead()
                } catch { /* noop */ }
            }
        },
    }
})